/**
 * Fixed copy — spec §7.5.
 *
 * These strings carry legal and safety weight. They are centrally defined, not
 * owner-editable, and not paraphrasable by implementers. Changing one is a spec
 * revision, not a copy tweak.
 *
 * `pnpm --filter @pm/api test` runs `vocabulary.test.ts`, which greps the web and
 * admin sources for the forbidden transactional vocabulary below. That test is the
 * enforcement mechanism for §9.4 point 1.
 */

/**
 * §9.4 / §7.5 vocabulary constraint. The platform never uses transactional verbs
 * for the rental, because it processes no rental transaction.
 *
 * Each entry is matched case-insensitively as a whole word against user-visible
 * strings in rental contexts.
 */
export const FORBIDDEN_RENTAL_VOCABULARY = [
  'pay',
  'checkout',
  'buy',
  'book',
  'book now',
  'rent now',
  'price', // use "asking price" / "declared price"
  'total',
  'amount due',
  'escrow',
  'protected',
  'guaranteed',
  'secure payment',
  'seller',
  'buyer',
] as const;

/** The replacements the product does use. */
export const RENTAL_VOCABULARY = {
  requestAction: 'Request',
  requestActionLong: 'Request this bot',
  price: 'Asking price',
  priceAlt: 'Declared price',
  owner: 'Owner',
  renter: 'Renter',
} as const;

/**
 * FR-4.2 — shown on the rental request screen. Non-dismissible; requires an
 * explicit acknowledgement action. Not a pre-checked box, not a link to terms.
 */
export const RENTAL_DISCLOSURE = {
  heading: 'How payment works here',
  intro:
    "You arrange payment directly with the owner. This platform doesn't collect, hold, or transfer the rental price, and takes no cut of it.",
  leadIn: 'That means:',
  bullets: [
    "We can't refund a rental, because we never receive the money.",
    "We don't verify that the owner received your payment, or that you sent it.",
    "If the bot's connected account is suspended during your rental, access ends and we can't compensate you.",
    "We don't mediate disagreements about the rental price.",
  ],
  whatWeDo:
    'What we do provide: scoped access that ends when the rental ends, a full log of everything the bot did, and reviews from previous rentals.',
  acknowledgeLabel: 'I understand',
} as const;

/** FR-4.3 — shown when an Owner approves a rental request. */
export function ownerApprovalCopy(endAt: string): string {
  return [
    `Approving starts the rental and gives this renter the permissions in your listing until ${endAt}.`,
    '',
    "Only approve if you've received whatever payment you agreed to. We don't check, and we can't reverse it.",
  ].join('\n');
}

/** §5.3 TERMINATED_ACCOUNT_FAILURE notification, sent to both parties. */
export function accountFailureCopy(args: {
  botName: string;
  platform: string;
  timestamp: string;
  daysEarly: number;
  actionCount: number;
  downloadWindowDays: number;
}): string {
  return [
    `The connected account for ${args.botName} was suspended by ${args.platform} at ${args.timestamp}, so this rental has ended ${args.daysEarly} days early.`,
    '',
    `This wasn't caused by anything you did. The bot posted ${args.actionCount} times during your rental — the full log is attached and stays available for ${args.downloadWindowDays} days.`,
    '',
    "We don't hold the rental payment, so there's nothing for us to refund. Any arrangement about the unused time is between you and the owner.",
  ].join('\n');
}

/** §8.4 credit expiry warning, sent at 30 and 7 days. */
export function creditExpiryCopy(count: number, date: string): string {
  return `${count} generation credits expire on ${date}. Credits can't be refunded or transferred after expiry.`;
}

/**
 * §11.2 — owner-facing disclosure shown at social-account connect time. Plainly
 * worded and not buried in terms.
 */
export const CONNECT_ACCOUNT_DISCLOSURE = {
  heading: 'What connecting this account means',
  bullets: [
    'This platform will operate this account: it will post, comment and read on your behalf.',
    'During a rental, a third party directs its activity — but only within the permissions your listing grants.',
    'You can revoke this at any time from your own settings here, without asking anyone.',
    "The account stays subject to the social platform's own rules and enforcement, including suspension.",
  ],
  acknowledgeLabel: 'I understand — connect the account',
} as const;

/** §7.6 empty states. Each distinguishes "nothing yet" from "filtered out" or "failed". */
export const EMPTY_STATES = {
  marketplaceNoListings: {
    heading: 'No bots are listed yet',
    body: 'The first people here are owners. Build a persona, list it, and set your own asking price.',
    cta: 'Create a bot',
  },
  marketplaceNoMatches: {
    heading: 'Nothing matches every filter',
    // The narrowing filter is computed server-side and interpolated by the caller.
    bodyTemplate: 'The {filter} filter excluded the most results ({count} bots).',
    cta: 'Relax that filter',
  },
  ownerNoBots: {
    heading: 'You have no bots yet',
    body: 'A bot is a persona: a character description, a connected account, and a posting style.',
    cta: 'Create a bot',
  },
  renterNoRentals: {
    heading: "You aren't renting any bots",
    body: 'Browse the marketplace to find a persona and audience to borrow.',
    cta: 'Browse bots',
  },
  consoleNoActions: {
    heading: 'Nothing posted yet',
    body: 'Generate your first piece of content. The cost is quoted before anything is charged.',
    cta: 'Generate',
  },
  libraryNoAssets: {
    heading: 'The library is empty',
    body: 'Nothing has been generated for this bot yet.',
  },
  libraryAllFiltered: {
    heading: 'No assets match these filters',
    body: 'The library is not empty — clear a filter to see the rest.',
  },
  activityEmptyWindow: {
    // Window is interpolated; an empty log reads as a failure unless the window is stated.
    bodyTemplate: 'Nothing happened between {from} and {to}.',
  },
  feedEmpty: {
    heading: 'The feed came back empty',
    body: 'The connected account returned no results for this query.',
  },
  feedAdapterFailure: {
    heading: "Couldn't reach {platform}",
    body: 'This is an adapter failure, not an empty feed. Retry, or check the account health above.',
  },
  feedRateLimited: {
    heading: 'Rate limited by {platform}',
    bodyTemplate: 'Quota resets at {resetAt}. Cached results below are {age} old.',
  },
  listeningNoRules: {
    heading: 'No hashtags or keywords tracked yet',
    body: 'Add a rule to search for matching posts and draft replies automatically.',
    cta: 'Add a rule',
  },
  listeningNoMatches: {
    heading: 'No matches yet',
    body: 'The next poll checks every enabled rule. Matches and their drafted replies show up here.',
  },
} as const;
